// import uid from "common/utils/uid";
import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

export function formulaNew(formula: string): any {
  return { type: "FORMULA", formula };
}

function isFormula(v: any): boolean {
  return !!v && typeof v === "object" && v.hasOwnProperty("type") && v.type === "FORMULA";
}

function formulaInvert(value: any): any {
  if (isFormula(value)) {
    if (value.formula && value.formula.trim().startsWith("not (")) {
      return formulaNew(value.formula.trim().slice(5, -1));
    } else {
      return formulaNew(`not (${value.formula.trim()})`);
    }
  } else {
    return value;
  }
}

export function simplifyExpression(expression: any): any {
  if (!isFormula(expression)) {
    return expression;
  }
  if (expression.formula === "not (false)") {
    return true;
  } else if (expression.formula === "not (true)") {
    return false;
  } else {
    return expression;
  }
}

interface ConditionNode {
  type: "condition";
  condition: string;
}

interface AndNode {
  type: "and";
  left: ExpressionNode;
  right: ExpressionNode;
}

interface OrNode {
  type: "or";
  left: ExpressionNode;
  right: ExpressionNode;
}

type ExpressionNode = ConditionNode | AndNode | OrNode;

function parseExpressionToTree(formula: string): ExpressionNode {
  const tokens = formula.match(/\w+|==|!=|>=|<=|>|<|and|or|\(|\)|"(?:[^"\\]|\\.)*"/g) || [];

  let index = 0;

  function parseOr(): ExpressionNode {
    let left = parseAnd();

    while (index < tokens.length && tokens[index] === "or") {
      index++; // consume 'or'
      const right = parseAnd();
      left = { type: "or", left, right };
    }

    return left;
  }

  function parseAnd(): ExpressionNode {
    let left = parseCondition();

    while (index < tokens.length && tokens[index] === "and") {
      index++; // consume 'and'
      const right = parseCondition();
      left = { type: "and", left, right };
    }

    return left;
  }

  function parseCondition(): ExpressionNode {
    if (index < tokens.length && tokens[index] === "(") {
      index++; // consume '('
      const expr = parseOr();
      if (index < tokens.length && tokens[index] === ")") {
        index++; // consume ')'
      }
      return expr;
    }

    // Parse a single condition: variable operator value
    if (index + 2 < tokens.length) {
      const variable = tokens[index++];
      const operator = tokens[index++];
      const value = tokens[index++];
      return { type: "condition", condition: `${variable} ${operator} ${value}` };
    }

    throw new Error("Invalid condition");
  }

  return parseOr();
}

function treeToString(node: ExpressionNode): string {
  if (node.type === "condition") {
    return node.condition;
  }

  const left = treeToString(node.left);
  const right = treeToString(node.right);

  if (node.type === "and") {
    return `(${left} and ${right})`;
  } else {
    return `(${left} or ${right})`;
  }
}

function negateTree(node: ExpressionNode): ExpressionNode {
  if (node.type === "condition") {
    // Invert the condition
    const condition = node.condition;
    // Equality/inequality first using safe tokens.
    let inverted = condition
      .replace(/==/g, "__EQ__")
      .replace(/!=/g, "==")
      .replace(/__EQ__/g, "!=");

    // Relational operators with non-overlapping placeholders.
    inverted = inverted
      .replace(/<=/g, "__LE__")
      .replace(/>=/g, "__GE__")
      .replace(/</g, "__LT__")
      .replace(/>/g, "__GT__")
      // Now substitute inverted forms
      .replace(/__LE__/g, ">")
      .replace(/__GE__/g, "<")
      .replace(/__LT__/g, ">=")
      .replace(/__GT__/g, "<=");

    return { type: "condition", condition: inverted };
  }

  // Apply De Morgan's laws: not (A and B) = not A or not B
  // Apply De Morgan's laws: not (A or B) = not A and not B
  const negatedLeft = negateTree(node.left);
  const negatedRight = negateTree(node.right);

  if (node.type === "and") {
    return { type: "or", left: negatedLeft, right: negatedRight };
  } else {
    return { type: "and", left: negatedLeft, right: negatedRight };
  }
}

export function formulaApplyNots(formula: any): string {
  if (formula.formula && formula.formula.trim().startsWith("not (")) {
    // Remove the "not (" wrapper.
    const innerFormula = formula.formula.slice(5, -1);

    // Parse into tree structure that handles operator precedence correctly
    const tree = parseExpressionToTree(innerFormula);

    // Apply De Morgan's laws to the tree
    const negatedTree = negateTree(tree);

    // Convert back to string
    return treeToString(negatedTree);
  } else {
    return formula.formula;
  }
}

function formulaNotAnd(formulas: any[]): boolean | any {
  if (formulas.length === 0) {
    return false;
  }
  if (formulas.length === 1) {
    return formulaInvert(formulas[0]);
  }
  const invertedFormulas = formulas.map((formula) => formulaApplyNots(formula));
  const formulaValue = `not (${invertedFormulas.join(" and ")})`;
  return formulaNew(formulaValue);
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_475: DocMigration = {
  maxSchemaVersion: "0.0.475",
  description: "Copy ceilingHeightOffset and ceilingMaterial from StaticRoom to ceilings",
  apply(json: any) {
    if (!json.ceilings) {
      json.ceilings = {};
    }

    const { rooms, ceilings } = json;

    const roomIds = Object.keys(rooms);
    for (const roomId of roomIds) {
      const room = rooms[roomId];
      const { ceilingHeightOffset, ceilingMaterial } = room;
      // Find all ceilings that belong to this room.
      const ceilingIds = Object.keys(ceilings);

      const disabledFormulas: any[] = [];
      let anyCeilingEnabled = false;
      for (const ceilingId of ceilingIds) {
        const ceiling = ceilings[ceilingId];
        if (ceiling.parentId === roomId) {
          // Set the ceiling's offset and material to the room's values.
          ceiling.ceilingVerticalOffset = ceilingHeightOffset;
          ceiling.ceilingMaterial = ceilingMaterial;
          const disabled = simplifyExpression(ceiling.disabled);
          if (!disabled) {
            anyCeilingEnabled = true;
          } else if (isFormula(disabled)) {
            disabledFormulas.push(disabled);
          }
        }
      }

      // If it's possible there are no enabled ceilings, add a flat ceiling.
      if (!anyCeilingEnabled) {
        const id = uid();
        const flatCeiling = {
          id,
          name: "Flat Ceiling",
          entityType: "CEILING",
          type: "FLAT",
          parentId: roomId,
          sortIndex: 1,
          tags: {
            addToElectricalPlan: true,
            addToFloorPlan: true,
            addToStructuralPlan: true,
            addToShowroomPlan: true,
            addToBrochurePlan: true,
          },
          disabled: formulaNotAnd(disabledFormulas),
          ceilingMaterial: ceilingMaterial,
          ceilingVerticalOffset: ceilingHeightOffset,
        };

        // Add the ceiling to the ceilings object
        ceilings[id] = flatCeiling;
      }

      delete room.ceilingHeightOffset;
      delete room.ceilingMaterial;
    }
    return json;
  },
};

export default migration_0_0_475;
